import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Anchor, Button, Checkbox, Container, Group, NumberInput, SimpleGrid, Skeleton, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import AgentAssignmentBlock from '@/components/app/sell/form/AgentAssignmentBlock';
import FileList from '@/components/app/sell/form/FileList';
import LoadingConditionsFields from '@/components/app/sell/form/LoadingConditionsFields';
import { FIELD_LABEL, fieldError, focusSellField, sellFieldId } from '@/components/app/sell/form/sellFormFields';
import {
  buildLoadingConditions,
  COMMENT_MAX,
  DECLARATION_NO_MAX,
  initialSellForm,
  loadingDateErrors,
  logisticsProblems,
  numberProblem,
  WEIGHT_RANGE,
  type SellField,
  type SellFieldProblem,
  type SellFormState,
} from '@/components/app/sell/form/sellFormState';
import { sellerConditionRows } from '@/components/app/sell/SaleOfferTerms';
import UploadZone from '@/components/app/company/UploadZone';
import FormMessage from '@/components/auth/FormMessage';
import BulletinSection from '@/components/common/BulletinSection';
import FactsStrip, { type Fact } from '@/components/common/FactsStrip';
import FormErrorsSummary, { type FormErrorItem } from '@/components/common/FormErrorsSummary';
import IdText from '@/components/common/IdText';
import PageTitle from '@/components/common/PageTitle';
import TermList, { type TermItem } from '@/components/common/TermList';
import { orderService } from '@/api/services/OrderService';
import { sellService } from '@/api/services/SellService';
import { getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { useIsAgent } from '@/hooks/useRoles';
import { decodeEntities, formatMoney, formatOrgName, formatProperty } from '@/lib/format';
import { isoToRu, todayIso } from '@/lib/date';
import { ROUTES } from '@/routes/paths';
import {
  percentKeyFromPrepayment,
  prepaymentTypeFromPercent,
  type PurchaseOrderView,
  type SellDealParams,
  type SellPrepaymentType,
} from '@/types/sellDeal';
import classes from './PurchaseOfferPreviewPage.module.css';

const POLL_INTERVAL_MS = 800;
const POLL_DEADLINE_MS = 60_000;

class DealSuspendedError extends Error {}
class DealTimeoutError extends Error {}

const CREDIT_TYPE_KEYS: Readonly<Record<string, string>> = {
  BY_AVERAGE_VALUE: 'app.buy.terms.creditAverage',
  BY_EACH_DAY: 'app.buy.terms.creditEachDay',
};

/** Раздел страницы, к которому ведёт строка «Осталось» и сводка. */
type CounterSection = 'conditions' | 'declaration' | 'agent' | 'agree';

const SECTION_TITLE: Record<CounterSection, string> = {
  conditions: 'app.sell.deal.myConditions',
  declaration: 'app.sell.form.sectionDeclaration',
  agent: 'app.sell.deal.agent.title',
  agree: 'app.sell.form.sectionAgree',
};

const DECLARATION_FIELDS: readonly SellField[] = ['declarationNo', 'declarationDate', 'declarationFiles'];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Параметры страницы из адреса. */
export interface CounterParams {
  readonly orderId: number;
  readonly orderVersion: number;
  /** Своё предложение продавца: с ним условия погрузки и декларацию берёт сервер. Без него — свободный режим. */
  readonly fromSaleOrderId?: number;
  readonly fromAddress: string;
  readonly fromAddressTitle: string;
  readonly goods?: string;
  readonly prepayment: SellPrepaymentType;
  readonly advantage?: number;
  readonly myPrice?: number;
  readonly prefillWeightTon?: number;
  readonly hasScales?: boolean;
  readonly trawls?: boolean;
  readonly trailers?: boolean;
  readonly scalesLength?: number;
}

const POSITIVE_INT = /^[1-9]\d*$/;
const NON_NEGATIVE_INT = /^\d+$/;

function optionalNumber(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === '') {
    return undefined;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

function optionalBool(raw: string | null): boolean | undefined {
  return raw === 'true' ? true : raw === 'false' ? false : undefined;
}

/**
 * Разбор адреса страницы. Номер и версия заявки, адрес и название базиса обязательны; номер своего предложения, если
 * задан, должен быть номером. Неверная ссылка — `null` («не найдено»), а не тихий переход в свободный режим.
 */
export function parseCounterParams(params: URLSearchParams): CounterParams | null {
  const orderId = params.get('purchaseOrderId') ?? '';
  const orderVersion = params.get('purchaseOrderVersion') ?? '';
  const fromSale = params.get('fromSaleOrderId');
  const fromAddress = params.get('fromAddress')?.trim();
  const fromAddressTitle = params.get('fromAddressTitle')?.trim();
  // Версии может не быть: ссылка «Продать по ней» кладёт её, только если её прислал сервер, — тогда, как и прежде,
  // берётся 0, и актуальную версию сообщит ответ сервера. Нечисловая версия — неверная ссылка.
  if (!POSITIVE_INT.test(orderId) || (orderVersion !== '' && !NON_NEGATIVE_INT.test(orderVersion)) || !fromAddress || !fromAddressTitle) {
    return null;
  }
  if (fromSale != null && fromSale !== '' && !POSITIVE_INT.test(fromSale)) {
    return null;
  }
  const bound = fromSale != null && fromSale !== '';
  return {
    orderId: Number(orderId),
    orderVersion: orderVersion === '' ? 0 : Number(orderVersion),
    fromSaleOrderId: bound ? Number(fromSale) : undefined,
    fromAddress,
    fromAddressTitle,
    goods: params.get('goods') ?? undefined,
    prepayment: params.get('prepayment') === 'PERCENT_100' ? 'PERCENT_100' : 'PERCENT_0',
    // Выгода и прежняя цена есть только у ссылки из своего предложения: в свободном режиме сравнивать не с чем.
    advantage: bound ? optionalNumber(params.get('advantage')) : undefined,
    myPrice: bound ? optionalNumber(params.get('myPrice')) : undefined,
    prefillWeightTon: optionalNumber(params.get('prefillWeightTon')),
    hasScales: optionalBool(params.get('hasScales')),
    trawls: optionalBool(params.get('trawls')),
    trailers: optionalBool(params.get('trailers')),
    scalesLength: optionalNumber(params.get('scalesLength')),
  };
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Anchor component={Link} to={ROUTES.APP_SELL} c="gray.7" fz="sm" underline="never" style={{ alignSelf: 'flex-start' }}>
      <Group gap={4} wrap="nowrap">
        <IconArrowLeft size={16} aria-hidden />
        {t('app.sell.deal.back')}
      </Group>
    </Anchor>
  );
}

/** Экран без данных заявки: заголовок и сообщение. */
function MessageScreen({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <BackLink />
        <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
          {title}
        </PageTitle>
        {children}
      </Stack>
    </Container>
  );
}

// Страница пересоздаётся при смене адреса: поручение, согласия и введённые условия относятся к одной заявке
// покупателя и не должны перейти на другую (переход назад и вперёд по истории не размонтирует страницу сам).
export default function PurchaseOfferPreviewPage() {
  const isAgent = useIsAgent();
  const [params] = useSearchParams();
  const key = params.toString();
  const parsed = useMemo(() => parseCounterParams(new URLSearchParams(key)), [key]);
  const { t } = useTranslation();
  // Агент не заключает сделок как сторона — он посредник; прямой заход по адресу возвращает его к списку.
  if (isAgent) {
    return <Navigate to={ROUTES.APP_SELL} replace />;
  }
  if (!parsed) {
    return (
      <MessageScreen title={t('app.sell.deal.title')}>
        <FormMessage>{t('app.sell.deal.notFound')}</FormMessage>
      </MessageScreen>
    );
  }
  return <CounterOfferPage key={key} p={parsed} />;
}

// Встречное предложение продавца («Бюллетень», образец b-deal): продавец отвечает на заявку покупателя. Шапка с ценой,
// полоса фактов, условия покупателя, свои условия (из своего предложения или вводом), комментарий, поручение агенту,
// согласия и «Продать». Кнопка доступна всегда: до попытки под ней строка «Осталось», после — сводка со ссылками.
function CounterOfferPage({ p }: { readonly p: CounterParams }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const freeMode = p.fromSaleOrderId == null;

  const [comment, setComment] = useState('');
  const [agreeSupply, setAgreeSupply] = useState(false);
  const [agreeOferta, setAgreeOferta] = useState(false);
  const [assignmentFileId, setAssignmentFileId] = useState<number | null>(null);
  const [assignmentSigned, setAssignmentSigned] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Увеличивается, когда сервер сообщил об изменении заявки: подписанное поручение больше не годится.
  const [resetNonce, setResetNonce] = useState(0);
  const submitErrorRef = useRef<HTMLDivElement>(null);

  // Свои условия — только в свободном режиме. Оборудование берём из ссылки «Продать по ней», чтобы цена сделки
  // считалась по тем же параметрам, что продавец видел в ориентире; отсутствующие остаются по умолчанию.
  const [logistics, setLogistics] = useState<SellFormState>(() => {
    const base = initialSellForm();
    return {
      ...base,
      hasScales: p.hasScales ?? base.hasScales,
      trawls: p.trawls ?? base.trawls,
      trailers: p.trailers ?? base.trailers,
      scalesLength: p.scalesLength ?? base.scalesLength,
    };
  });
  const set = useCallback((patch: Partial<SellFormState>) => setLogistics((s) => ({ ...s, ...patch })), []);

  const shipmentModes = useQuery({
    queryKey: ['shipmentModes'] as const,
    queryFn: ({ signal }) => sellService.getShipmentModes(signal),
    enabled: isAuthenticated && freeMode,
    staleTime: 10 * 60_000,
  });

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Оборудование влияет на цену только в свободном режиме; с предложением сервер берёт его из предложения.
  const viewHasScales = freeMode ? logistics.hasScales : undefined;
  const viewHasTrawl = freeMode ? logistics.trawls : undefined;
  const viewHasTrailer = freeMode ? logistics.trailers : undefined;
  const view = useQuery({
    queryKey: [
      'purchaseOrderView',
      p.orderId,
      p.orderVersion,
      p.fromSaleOrderId,
      p.fromAddress,
      viewHasScales,
      viewHasTrawl,
      viewHasTrailer,
    ] as const,
    queryFn: ({ signal }): Promise<PurchaseOrderView> =>
      orderService.getPurchaseOrderView(
        {
          orderId: p.orderId,
          orderVersion: p.orderVersion,
          fromSaleOrderId: p.fromSaleOrderId,
          fromAddress: p.fromAddress,
          fromAddressTitle: p.fromAddressTitle,
          hasScales: viewHasScales,
          hasTrawl: viewHasTrawl,
          hasTrailer: viewHasTrailer,
        },
        signal,
      ),
    enabled: isAuthenticated,
    staleTime: 30_000,
    retry: false,
    // Переключение оборудования меняет только цену: прежние данные остаются на экране, форма не пропадает.
    placeholderData: keepPreviousData,
  });
  // Последние полученные данные: если перезагрузка после переключения оборудования не удалась, страница и введённые
  // условия остаются на экране с сообщением об ошибке, а цена считается устаревшей до успешного повтора.
  const lastView = useRef<PurchaseOrderView | undefined>(undefined);
  if (view.data && !view.isPlaceholderData) {
    lastView.current = view.data;
  }
  const data = view.data ?? lastView.current;
  const order = data?.order;
  // Сбой фоновой перезагрузки того же запроса оставляет его данные действительными; устарела цена только тогда,
  // когда на экране данные прежних условий (запрос новых не удался).
  const reloadFailed = view.isError && view.data == null && data != null;
  const reloadGone = reloadFailed && [404, 410, 424].includes(getStatus(view.error) ?? 0);
  const priceStale = view.isPlaceholderData || reloadFailed;

  // Предоплату задаёт покупатель. Берём вариант из ссылки (его выбрал расчёт лучшей цены); если в заявке его нет —
  // первый доступный.
  const prepayment: SellPrepaymentType = useMemo(() => {
    const opts = data?.priceByOptions ?? {};
    if (opts[percentKeyFromPrepayment(p.prepayment)]) {
      return p.prepayment;
    }
    const firstKey = Object.keys(opts)[0];
    return firstKey ? prepaymentTypeFromPercent(firstKey) : p.prepayment;
  }, [data, p.prepayment]);

  // Без цены блок поручения скрыт. Если поручение уже было, оно сбрасывается: при появлении цены блок начнёт заново,
  // а не примет прежний документ за подписанный.
  const hasPrice = data?.priceByOptions?.[percentKeyFromPrepayment(prepayment)] != null;
  useEffect(() => {
    if (data && !hasPrice && !priceStale) {
      setAssignmentFileId(null);
      setAssignmentSigned(false);
    }
  }, [data, hasPrice, priceStale]);

  // Объём в свободном режиме подставляется один раз: из формы «Продать по ней», иначе — объём заявки. Если продавец
  // очистил поле, повторная загрузка данных его не заполнит.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!freeMode || prefilled.current) {
      return;
    }
    const weight = p.prefillWeightTon ?? data?.order?.weightTon;
    if (weight != null) {
      prefilled.current = true;
      setLogistics((s) => (s.weightTon === '' ? { ...s, weightTon: weight } : s));
    }
  }, [freeMode, data, p.prefillWeightTon]);

  // Версия заявки — из ответа сервера, если он её прислал: после «Заявка изменилась» (412) повторная загрузка даёт
  // актуальную версию, и новое поручение и сделка составляются по ней, а не по версии из ссылки.
  const orderVersion = data?.order.version ?? p.orderVersion;
  const dealParams: SellDealParams = useMemo(() => {
    const base = {
      orderId: p.orderId,
      orderVersion,
      creationSection: 'SELL_BEST_PRICE' as const,
      isBestPrice: true,
      userComment: comment.trim(),
      prepaymentType: prepayment,
    };
    // С предложением логистику и адрес определяет сервер (условия погрузки и адрес слать нельзя).
    if (p.fromSaleOrderId != null) {
      return { ...base, fromSaleOrderId: p.fromSaleOrderId };
    }
    return {
      ...base,
      weightTon: logistics.weightTon === '' ? undefined : Number(logistics.weightTon),
      loadingConditions: buildLoadingConditions(logistics),
      partnerAddress: p.fromAddress,
      partnerAddressTitle: p.fromAddressTitle,
      declarationNo: logistics.declarationNo.trim() || undefined,
      declarationDate: isoToRu(logistics.declarationDate) ?? undefined,
      declarationFileIds: logistics.declarationFiles.map((file) => file.id),
    };
  }, [p, orderVersion, comment, prepayment, logistics]);

  // Данные, из которых составлено поручение: их изменение аннулирует подписанное поручение. Комментарий в документ
  // поручения не входит, поэтому его правка поручение не сбрасывает.
  const signatureKey = useMemo(
    () =>
      JSON.stringify({
        p: prepayment,
        v: orderVersion,
        n: resetNonce,
        ...(freeMode
          ? {
              lc: buildLoadingConditions(logistics),
              w: logistics.weightTon,
              dn: logistics.declarationNo.trim(),
              dd: logistics.declarationDate,
              df: logistics.declarationFiles.map((file) => file.id),
            }
          : {}),
      }),
    [prepayment, orderVersion, resetNonce, freeMode, logistics],
  );

  const onActionError = (error: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')),
    });
  };

  // Декларация (свободный режим). Список обновляется от текущего состояния: параллельная загрузка или удаление во
  // время загрузки не теряют файлы.
  const uploadDeclaration = useMutation({
    mutationFn: (files: File[]) => sellService.uploadDeclaration(files),
    onSuccess: (uploaded) => setLogistics((s) => ({ ...s, declarationFiles: [...s.declarationFiles, ...uploaded] })),
    onError: onActionError,
  });
  const removeDeclaration = useMutation({
    mutationFn: (fileId: number) => sellService.deleteSaleFile(fileId),
    onSuccess: (_, fileId) => setLogistics((s) => ({ ...s, declarationFiles: s.declarationFiles.filter((file) => file.id !== fileId) })),
    onError: onActionError,
  });
  const download = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: onActionError,
  });

  const sell = useMutation({
    mutationFn: async (): Promise<number> => {
      const processId = await orderService.createDealFromPurchase({ ...dealParams, agentAssignmentFileId: assignmentFileId! });
      // Сделка уже создаётся: сбой одного опроса не означает отказа, поэтому опрос повторяется до крайнего срока, а
      // по его истечении продавец уходит в «Мои сделки». Повторно отправить заявку на сделку страница не даёт.
      const deadline = Date.now() + POLL_DEADLINE_MS;
      while (Date.now() < deadline && mountedRef.current) {
        await sleep(POLL_INTERVAL_MS);
        let status;
        try {
          status = await orderService.getBargainCreateStatus(processId);
        } catch (error) {
          // Сеть, тайм-аут и ошибки сервера — временные: опрос продолжается. Отказ 4xx (процесс неизвестен, нет
          // доступа) повтором не исправить.
          const code = getStatus(error);
          if (code != null && code >= 400 && code < 500 && code !== 408 && code !== 429) {
            throw error;
          }
          continue;
        }
        if (status.created && status.bargainId != null) {
          return status.bargainId;
        }
        if (status.suspended) {
          throw new DealSuspendedError();
        }
      }
      throw new DealTimeoutError();
    },
    onSuccess: (bargainId) => {
      if (!mountedRef.current) {
        return;
      }
      notifications.show({ color: 'lime', message: t('app.buy.offer.dealCreated', { id: bargainId }) });
      navigate(`${ROUTES.APP_DEALS}/${bargainId}`);
    },
    onError: (error) => {
      if (!mountedRef.current) {
        return;
      }
      if (error instanceof DealTimeoutError) {
        notifications.show({ color: 'wheat', message: t('app.buy.offer.dealTimeout') });
        navigate(ROUTES.APP_DEALS);
        return;
      }
      const status = getStatus(error);
      if (status === 423) {
        notifications.show({ color: 'wheat', message: t('app.banner.certification') });
        navigate(ROUTES.APP_COMPANY_ACCREDITATION);
        return;
      }
      if (status === 412) {
        // Заявка изменилась: прежнее поручение составлено по старым условиям.
        setResetNonce((n) => n + 1);
        void view.refetch();
      }
      setSubmitError(
        error instanceof DealSuspendedError
          ? t('app.buy.offer.dealSuspended')
          : status === 410 || status === 424
            ? t('app.sell.deal.gone')
            : status === 412
              ? t('app.buy.offer.changed')
              : status === 422
                ? t('app.buy.offer.missingData')
                : isServiceUnavailable(error)
                  ? t('auth.errors.unavailable')
                  : (getApiMessage(error) ?? t('auth.errors.generic')),
      );
    },
  });

  // Кнопка с индикатором загрузки теряет фокус, поэтому ошибку показываем сообщением и переводим на него фокус.
  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.focus();
    }
  }, [submitError]);

  // Проблемы своих условий (свободный режим) в порядке полей на экране.
  const problems = useMemo((): SellFieldProblem[] => {
    if (!freeMode) {
      return [];
    }
    const list: SellFieldProblem[] = [];
    const weight = numberProblem(logistics.weightTon, WEIGHT_RANGE);
    if (weight) {
      list.push({ field: 'weight', reason: weight });
    }
    list.push(...logisticsProblems(logistics));
    if (!logistics.declarationNo.trim()) {
      list.push({ field: 'declarationNo', reason: 'required' });
    }
    if (!logistics.declarationDate) {
      list.push({ field: 'declarationDate', reason: 'required' });
    }
    if (logistics.declarationFiles.length === 0) {
      list.push({ field: 'declarationFiles', reason: 'required' });
    }
    return list;
  }, [freeMode, logistics]);
  const problemMap = useMemo(() => new Map(problems.map((problem) => [problem.field, problem])), [problems]);
  const dateErrors = useMemo(() => loadingDateErrors(logistics), [logistics]);
  const fieldErr = (field: SellField, range?: Parameters<typeof fieldError>[4]): string | undefined =>
    fieldError(problemMap, field, attempted, t, range);

  if (view.isError && !data) {
    const status = getStatus(view.error);
    const title = p.goods ?? t('app.sell.deal.title');
    if (status === 404 || status === 403) {
      return (
        <MessageScreen title={title}>
          <FormMessage>{t('app.sell.deal.notFound')}</FormMessage>
        </MessageScreen>
      );
    }
    if (status === 410 || status === 424) {
      return (
        <MessageScreen title={title}>
          <FormMessage tone="info">
            <Group gap="sm">
              <span>{t('app.sell.deal.gone')}</span>
              <Anchor component={Link} to={ROUTES.APP_SELL} underline="always">
                {t('app.sell.deal.back')}
              </Anchor>
            </Group>
          </FormMessage>
        </MessageScreen>
      );
    }
    return (
      <MessageScreen title={title}>
        <FormMessage>
          <Group gap="sm">
            <span>{isServiceUnavailable(view.error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</span>
            <Button variant="default" size="compact-md" loading={view.isFetching} onClick={() => view.refetch()}>
              {t('common.retry')}
            </Button>
          </Group>
        </FormMessage>
      </MessageScreen>
    );
  }

  if (!data || !order) {
    // Скелет повторяет будущую разметку: шапку, полосу фактов и раздел условий.
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg" aria-busy="true" aria-label={t('app.sell.deal.loading')}>
          <BackLink />
          <Stack gap="sm">
            <Skeleton h={36} w={320} />
            <Skeleton h={20} w={260} />
          </Stack>
          <Skeleton h={64} />
          <Skeleton h={240} />
        </Stack>
      </Container>
    );
  }

  const priceInfo = data.priceByOptions?.[percentKeyFromPrepayment(prepayment)];
  const fcaPrice = priceInfo?.fcaPricePerTonWithoutNds;
  const weight = freeMode ? (logistics.weightTon === '' ? undefined : Number(logistics.weightTon)) : (data.weightTon ?? order.weightTon);
  // Итог продавцу: с предложением — расчёт сервера по объёму; в свободном режиме — цена × введённый объём.
  const total = freeMode ? (fcaPrice != null && weight != null ? fcaPrice * weight : undefined) : priceInfo?.fullPriceFCAWithoutNds;
  const advantage = p.advantage != null && p.myPrice != null ? Math.round(p.advantage) : 0;
  const quality = order.goodsPropertiesSet?.properties?.[0];
  const prepaymentText = t(prepayment === 'PERCENT_100' ? 'app.sell.prepayment100Short' : 'app.sell.prepayment0Short');

  // Года у закупки нет (решение 2026-11-04): в превью заявки покупателя год не показывается.
  const lead = [t('app.sell.deal.lead'), quality ? formatProperty(quality) : null]
    .filter(Boolean)
    .join(' · ');

  const from = formatOrgName(data.fromAddress?.title ?? p.fromAddressTitle);
  const to = formatOrgName(data.toAddress?.title ?? order.addressTitle) ?? order.address;
  const factList: (Fact | null)[] = [
    order.userInfo?.name
      ? {
          key: 'buyer',
          label: t('app.sell.deal.buyer'),
          value: formatOrgName(order.userInfo.name),
          note: order.userInfo.inn ? (
            <>
              {t('auth.fields.inn')} <IdText>{order.userInfo.inn}</IdText>
            </>
          ) : undefined,
          wide: true,
        }
      : null,
    { key: 'route', label: t('app.sell.deal.facts.route'), value: to ? `${from} → ${to}` : from, wide: true },
    weight != null ? { key: 'weight', label: t('app.sell.offer.facts.weight'), value: `${formatMoney(weight)}\u00a0${t('app.sell.tons')}` } : null,
    { key: 'prepayment', label: t('app.sell.offer.facts.prepayment'), value: prepaymentText },
    data.distance != null
      ? { key: 'distance', label: t('app.sell.deal.distance'), value: t('app.sell.deal.km', { value: formatMoney(data.distance) }) }
      : null,
  ];
  const facts = factList.filter((fact): fact is Fact => fact != null);

  const acceptance = order.qualityAcceptanceProperties ?? [];
  const creditKey = order.qualityCreditType ? CREDIT_TYPE_KEYS[order.qualityCreditType] : undefined;
  const buyerTerms: TermItem[] = [
    {
      key: 'acceptance',
      label: t('app.buy.terms.acceptance'),
      value: (
        <>
          <div>{t(order.qualityAcceptanceByGost === false ? 'app.sell.terms.acceptanceFlexible' : 'app.buy.terms.acceptanceGost')}</div>
          {acceptance.length > 0 ? (
            <div>
              {acceptance
                .map((property) => `${property.name}${property.gostValue ? ` ${decodeEntities(property.gostValue)}` : ''}`)
                .join(' · ')}
            </div>
          ) : null}
        </>
      ),
    },
    ...(order.qualityCreditType
      ? [{ key: 'credit', label: t('app.buy.terms.credit'), value: creditKey ? t(creditKey) : order.qualityCreditType }]
      : []),
    ...(order.userComment
      ? [{ key: 'comment', label: t('app.buy.terms.comment'), value: <span className={classes.preWrap}>{order.userComment}</span> }]
      : []),
  ];
  const sellerTerms = freeMode
    ? []
    : sellerConditionRows(data, t, {
        onDownload: (id) => download.mutate(id),
        downloadingId: download.isPending ? download.variables : undefined,
      });

  // Всё, что мешает сделке, в порядке разделов на экране.
  const assignmentReady = assignmentSigned && assignmentFileId != null;
  const blockers: { readonly label: string; readonly target: SellField | CounterSection; readonly section: CounterSection }[] = [
    ...problems.map(({ field }) => ({
      label: t(FIELD_LABEL[field]),
      target: field,
      section: DECLARATION_FIELDS.includes(field) ? ('declaration' as const) : ('conditions' as const),
    })),
    ...(!assignmentReady ? [{ label: t('app.sell.form.check.agent'), target: 'agent' as const, section: 'agent' as const }] : []),
    ...(!agreeSupply || !agreeOferta ? [{ label: t('app.sell.form.check.agree'), target: 'agree' as const, section: 'agree' as const }] : []),
  ];
  const remainingSections = [...new Set(blockers.map((blocker) => blocker.section))];
  const summaryItems: FormErrorItem[] = blockers.map((blocker) => ({
    label: blocker.label,
    onSelect: () => focusSellField(blocker.target as SellField),
  }));

  const handleSell = (): void => {
    setSubmitError(null);
    if (blockers.length > 0) {
      // Попытка с незаполненным: отмечаем поля, показываем сводку и ведём к первому, что нужно исправить.
      setAttempted(true);
      const first = blockers[0]!.target;
      requestAnimationFrame(() => focusSellField(first as SellField));
      return;
    }
    sell.mutate();
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Stack gap="sm">
          <BackLink />
          <div>
            <div className={classes.head}>
              <div style={{ minWidth: 0 }}>
                <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
                  {order.goodsType ?? p.goods ?? t('app.sell.deal.title')}
                </PageTitle>
                <p className={classes.lead}>{lead}</p>
              </div>
              {fcaPrice != null ? (
                <div className={classes.price} aria-busy={priceStale || undefined}>
                  <p className={classes.priceLabel}>{t('app.sell.deal.youGet')}</p>
                  <p className={classes.priceValue}>
                    {formatMoney(fcaPrice)} <span className={classes.priceUnit}>{t('app.sell.rubPerTon')}</span>
                  </p>
                  {advantage !== 0 && p.myPrice != null ? (
                    <p className={advantage > 0 ? classes.gain : classes.loss}>
                      {t(advantage > 0 ? 'app.sell.deal.aboveMine' : 'app.sell.deal.belowMine', {
                        diff: formatMoney(Math.abs(advantage)),
                        price: formatMoney(p.myPrice),
                      })}
                    </p>
                  ) : null}
                  {total != null && weight != null ? (
                    <p className={classes.priceTotal}>
                      {t('app.sell.deal.totalLine', { weight: formatMoney(weight), amount: formatMoney(total) })}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
            <FactsStrip facts={facts} />
          </div>
        </Stack>

        {reloadFailed ? (
          <FormMessage>
            <Group gap="sm">
              <span>
                {reloadGone
                  ? t('app.sell.deal.gone')
                  : isServiceUnavailable(view.error)
                    ? t('auth.errors.unavailable')
                    : t('app.sell.deal.priceError')}
              </span>
              {reloadGone ? null : (
                <Button variant="default" size="compact-md" loading={view.isFetching} onClick={() => view.refetch()}>
                  {t('common.retry')}
                </Button>
              )}
            </Group>
          </FormMessage>
        ) : null}
        {!priceInfo && !priceStale ? <FormMessage tone="info">{t('app.sell.deal.noPrice')}</FormMessage> : null}

        <BulletinSection title={t('app.sell.deal.termsTitle')}>
          <TermList items={buyerTerms} />
        </BulletinSection>

        {sellerTerms.length > 0 ? (
          <BulletinSection title={t('app.sell.deal.myConditions')}>
            <TermList items={sellerTerms} />
          </BulletinSection>
        ) : null}

        {freeMode ? (
          <>
            <BulletinSection title={t('app.sell.deal.myConditions')} spaced>
              <Stack gap="lg">
                <NumberInput
                  id={sellFieldId('weight')}
                  required
                  className={classes.weight}
                  label={t('app.sell.deal.weight')}
                  value={logistics.weightTon}
                  onChange={(value) => set({ weightTon: value === '' ? '' : Number(value) })}
                  allowNegative={false}
                  decimalScale={0}
                  clampBehavior="none"
                  error={fieldErr('weight', WEIGHT_RANGE)}
                />
                <LoadingConditionsFields
                  state={logistics}
                  set={set}
                  shipmentModes={shipmentModes.data ?? []}
                  dateErrors={dateErrors}
                  problems={problemMap}
                  attempted={attempted}
                />
              </Stack>
            </BulletinSection>

            <BulletinSection title={t('app.sell.form.sectionDeclaration')} spaced>
              <Stack gap="md">
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <TextInput
                    id={sellFieldId('declarationNo')}
                    required
                    label={t('app.sell.form.declaration.no')}
                    placeholder={t('app.sell.form.declaration.noPlaceholder')}
                    value={logistics.declarationNo}
                    maxLength={DECLARATION_NO_MAX}
                    onChange={(event) => set({ declarationNo: event.currentTarget.value })}
                    error={fieldErr('declarationNo')}
                  />
                  <TextInput
                    id={sellFieldId('declarationDate')}
                    required
                    type="date"
                    label={t('app.sell.form.declaration.date')}
                    value={logistics.declarationDate}
                    max={todayIso()}
                    onChange={(event) => set({ declarationDate: event.currentTarget.value })}
                    error={fieldErr('declarationDate')}
                  />
                </SimpleGrid>
                <div id={sellFieldId('declarationFiles')} tabIndex={-1} className={classes.target}>
                  <Stack gap="sm">
                    <FileList
                      files={logistics.declarationFiles}
                      onRemove={(id) => removeDeclaration.mutate(id)}
                      onDownload={(id) => download.mutate(id)}
                      removing={removeDeclaration}
                    />
                    <UploadZone
                      accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.gif"
                      multiple
                      loading={uploadDeclaration.isPending}
                      hint={t('app.sell.form.declaration.filesHint')}
                      error={fieldErr('declarationFiles') ? t('app.sell.form.check.declaration') : undefined}
                      onFiles={(files) => uploadDeclaration.mutate(files)}
                    />
                  </Stack>
                </div>
              </Stack>
            </BulletinSection>
          </>
        ) : null}

        {priceInfo || priceStale ? (
          <>
            <Textarea
              label={t('app.buy.offer.commentLabel')}
              placeholder={t('app.buy.offer.commentPlaceholder')}
              maxLength={COMMENT_MAX}
              autosize
              minRows={2}
              value={comment}
              onChange={(event) => setComment(event.currentTarget.value)}
            />

            <BulletinSection title={t('app.sell.deal.agent.title')} spaced>
              <div id={sellFieldId('agent')} tabIndex={-1} className={classes.target}>
                <AgentAssignmentBlock
                  ready={problems.length === 0 && !priceStale && !sell.isPending}
                  generate={() => orderService.generateBargainAgentAssignment(p.orderId, dealParams)}
                  sign={(fileId) => orderService.signBargainAgentAssignment(fileId)}
                  intro={t('app.sell.deal.agent.intro')}
                  needFillFirst={t('app.sell.deal.agent.needFillFirst')}
                  signatureKey={signatureKey}
                  fileId={assignmentFileId}
                  signed={assignmentSigned}
                  onChange={(fileId, signed) => {
                    setAssignmentFileId(fileId);
                    setAssignmentSigned(signed);
                  }}
                />
              </div>
            </BulletinSection>

            <BulletinSection title={t('app.sell.form.sectionAgree')} spaced>
              <Stack gap="sm" id={sellFieldId('agree')} tabIndex={-1} className={classes.target}>
                <Checkbox
                  checked={agreeSupply}
                  onChange={(event) => setAgreeSupply(event.currentTarget.checked)}
                  error={attempted && !agreeSupply ? t('auth.validation.acceptRequired') : undefined}
                  label={
                    <>
                      {t('app.buy.offer.agreeSupplyPrefix')}{' '}
                      <Anchor href={ROUTES.SUPPLY} target="_blank" inherit onClick={(event) => event.stopPropagation()}>
                        {t('app.buy.offer.agreeSupplyLink')}
                      </Anchor>
                      {t('app.buy.offer.agreeSupplySuffix')}
                    </>
                  }
                />
                <Checkbox
                  checked={agreeOferta}
                  onChange={(event) => setAgreeOferta(event.currentTarget.checked)}
                  error={attempted && !agreeOferta ? t('auth.validation.acceptRequired') : undefined}
                  label={t('app.buy.offer.agreeOferta')}
                />
              </Stack>
            </BulletinSection>

            <Stack gap="md">
              {submitError ? <FormMessage ref={submitErrorRef}>{submitError}</FormMessage> : null}

              {/* До попытки — спокойная строка «Осталось», после неё — сводка со ссылками к полям. */}
              {attempted ? (
                <FormErrorsSummary title={t('common.fixFormErrors')} items={summaryItems} />
              ) : remainingSections.length > 0 ? (
                <Text size="sm" c="gray.7">
                  {t('app.sell.form.remaining')} {remainingSections.map((section) => t(SECTION_TITLE[section])).join(' · ')}
                </Text>
              ) : null}

              <Group justify="flex-end" gap="sm" className={classes.actions}>
                {/* Пока сделка оформляется, уход прервал бы ожидание её создания: ссылку нельзя сделать неактивной,
                    поэтому на это время — неактивная кнопка. */}
                {sell.isPending ? (
                  <Button variant="default" size="md" disabled>
                    {t('common.cancel')}
                  </Button>
                ) : (
                  <Button component={Link} to={ROUTES.APP_SELL} variant="default" size="md">
                    {t('common.cancel')}
                  </Button>
                )}
                <Button size="md" loading={sell.isPending} disabled={priceStale} onClick={handleSell}>
                  {sell.isPending ? t('app.sell.deal.selling') : t('app.sell.deal.sell')}
                </Button>
              </Group>
            </Stack>
          </>
        ) : null}
      </Stack>
    </Container>
  );
}
